import React, { useState } from 'react';
import { Heart, MessageCircle, Share2, BookOpen, Users, Sparkles, TrendingUp, Clock, Play, Calendar, Star, Search as SearchIcon } from 'lucide-react';
import { Post, HealthArticle, ExpertSession } from '../types';
import OmniSearch from './OmniSearch';
import { PostCardSkeleton, ArticleCardSkeleton, SessionCardSkeleton } from './LoadingStates';
import { GenericEmptyState } from './EmptyStates';

interface HubProps {
  posts: Post[];
  articles: HealthArticle[];
  upcomingSessions: ExpertSession[];
  onPostInteraction: (postId: string, action: string) => void;
  onSearch: (query: string) => void;
  onNavigate: (section: string, data?: any) => void;
}

const Hub: React.FC<HubProps> = ({
  posts,
  articles,
  upcomingSessions,
  onPostInteraction,
  onSearch,
  onNavigate
}) => {
  const [activeTab, setActiveTab] = useState('for-you');
  const [isLoading, setIsLoading] = useState(false);

  const handleOmniSearch = (query: string) => {
    setIsLoading(true);
    onSearch(query);
    
    // Simulate search delay
    setTimeout(() => {
      onNavigate('discover', { 
        searchQuery: query,
        activeTab: 'articles',
        filterResults: true 
      });
      setIsLoading(false);
    }, 500);
  };

  const handleSuggestionSelect = (suggestion: any) => {
    setIsLoading(true);
    
    // Simulate loading delay
    setTimeout(() => {
      if (suggestion.type === 'article' || suggestion.type === 'drug') {
        onNavigate('discover', { 
          type: suggestion.type,
          searchQuery: suggestion.text,
          filterResults: true 
        });
      } else if (suggestion.type === 'expert') {
        onNavigate('spotlight', { 
          type: 'expert',
          expertId: suggestion.id 
        });
      } else if (suggestion.type === 'community') {
        onNavigate('community', { 
          searchQuery: suggestion.text 
        });
      }
      setIsLoading(false);
    }, 300);
  };

  const handleExpertSpotlightClick = (session: ExpertSession) => {
    onNavigate('spotlight', { 
      type: 'live-event',
      sessionId: session.id,
      openLiveEventPage: true 
    });
  };

  const handlePostClick = (post: Post) => {
    onNavigate('community', { 
      postId: post.id,
      openPostDetails: true 
    });
  };

  const handleArticleClick = (article: HealthArticle) => {
    onNavigate('discover', { 
      type: 'article',
      articleId: article.id,
      openArticleView: true 
    });
  };

  const QuickActionCard = ({ title, description, icon: Icon, color, onClick, index }: any) => (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl text-left transition-all duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1 ${color} group card-interactive animate-fade-in`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center space-x-3 mb-2">
        <Icon size={20} className="text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
        <h3 className="font-semibold text-white transition-all duration-200 group-hover:translate-x-1">{title}</h3>
      </div>
      <p className="text-white/80 text-sm transition-all duration-200 group-hover:text-white">{description}</p>
      <div className="absolute inset-0 bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </button>
  );

  const PostCard = ({ post, index }: { post: Post; index: number }) => (
    <div 
      onClick={() => handlePostClick(post)}
      className="card-interactive p-4 animate-slide-up dark:bg-gray-800 dark:border-gray-700"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      <div className="flex items-start space-x-3 mb-3">
        <div className="avatar-md transition-transform duration-300 group-hover:scale-110">
          {post.isAnonymous ? '?' : post.authorName.charAt(0)}
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-green-600 transition-colors duration-200">
              {post.isAnonymous ? 'Anonymous' : post.authorName}
            </h4>
            {post.isExpert && !post.isAnonymous && (
              <span className="badge-success animate-pulse">
                ✓ Expert
              </span>
            )}
            <span className="text-gray-500 dark:text-gray-400 text-xs">{post.timestamp}</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-xs">{post.category}</p>
        </div>
      </div>
      
      <p className="text-gray-800 dark:text-gray-200 mb-3 leading-relaxed text-clamp-3 transition-colors duration-200 group-hover:text-gray-900 dark:group-hover:text-white">{post.content}</p>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onPostInteraction(post.id, 'like');
            }}
            className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <Heart size={16} className="transition-transform duration-200 hover:scale-110" />
            <span className="text-sm">{post.reactions.length}</span>
          </button>
          <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
            <MessageCircle size={16} />
            <span className="text-sm">{post.comments.length}</span>
          </div>
        </div>
        <button 
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 hover:scale-110 active:scale-95"
        >
          <Share2 size={16} />
        </button>
      </div>
    </div>
  );

  const ArticleCard = ({ article, index }: { article: HealthArticle; index: number }) => (
    <div 
      onClick={() => handleArticleClick(article)}
      className="card-interactive p-4 animate-slide-up dark:bg-gray-800 dark:border-gray-700"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-start space-x-3">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-blue-500 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
          <BookOpen className="text-white transition-transform duration-300 group-hover:scale-110" size={20} />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1 text-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
            {article.title}
          </h4>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 text-clamp-2">{article.excerpt}</p>
          <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center space-x-1">
              <Clock size={12} />
              <span>{article.readTime} min</span>
            </span>
            <span>{article.views} views</span>
            <span className="badge-primary">
              {article.category}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const ExpertSpotlightBanner = ({ session }: { session: ExpertSession }) => (
    <div 
      onClick={() => handleExpertSpotlightClick(session)}
      className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 cursor-pointer hover:shadow-2xl transition-all duration-300 text-white group mb-6 hover:scale-105 hover:-translate-y-1 animate-float"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          <span className="font-medium">🔴 LIVE SOON</span>
        </div>
        <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full transition-all duration-200 group-hover:bg-white/30">
          {session.currentAttendees}/{session.maxAttendees} joined
        </span>
      </div>
      
      <h3 className="font-bold text-lg mb-2 group-hover:text-green-100 transition-colors duration-200">
        {session.title}
      </h3>
      <p className="text-white/90 mb-3 text-clamp-2">{session.description}</p>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="avatar-sm transition-transform duration-300 group-hover:scale-110">
            {session.expertName.charAt(0)}
          </div>
          <div>
            <p className="font-medium">{session.expertName}</p>
            <p className="text-white/80 text-sm">{session.category}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 text-white/90">
          <Calendar size={16} />
          <span className="text-sm">{session.scheduledDate}</span>
        </div>
      </div>
    </div>
  );

  const SessionCard = ({ session, index }: { session: ExpertSession; index: number }) => (
    <div 
      onClick={() => handleExpertSpotlightClick(session)}
      className="card-interactive p-4 animate-slide-up dark:bg-gray-800 dark:border-gray-700"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-green-700 dark:text-green-400 font-medium text-sm">Live Soon</span>
        </div>
        <span className="badge-success">
          {session.currentAttendees}/{session.maxAttendees}
        </span>
      </div>
      
      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2 text-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200">
        {session.title}
      </h4>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="avatar-sm transition-transform duration-300 group-hover:scale-110">
            {session.expertName.charAt(0)}
          </div>
          <span className="text-sm text-gray-700 dark:text-gray-300">{session.expertName}</span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{session.scheduledDate}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20 lg:pb-0 animate-fade-in">
      {/* Header */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-30 animate-slide-up">
        <div className="container-responsive py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="animate-slide-up">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Your Health Hub</h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Personalized health insights & community</p>
            </div>
            <div className="flex items-center space-x-2 animate-slide-up">
              <Sparkles className="text-green-500 animate-pulse" size={16} />
              <span className="text-xs text-gray-600 dark:text-gray-400">AI-Powered</span>
            </div>
          </div>
          
          {/* OmniSearch */}
          <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
            <OmniSearch
              onSearch={handleOmniSearch}
              onSuggestionSelect={handleSuggestionSelect}
              placeholder="🔍 Ask anything... 'Can I take paracetamol with ibuprofen?'"
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="container-responsive py-6">
        {/* Expert Spotlight Banner */}
        {upcomingSessions.length > 0 ? (
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <ExpertSpotlightBanner session={upcomingSessions[0]} />
          </div>
        ) : (
          <div className="animate-fade-in mb-6" style={{ animationDelay: '300ms' }}>
            <GenericEmptyState
              icon={<Calendar className="text-gray-400" size={48} />}
              title="No upcoming sessions"
              description="Check back later for expert live sessions"
              actionLabel="Browse Expert Directory"
              onAction={() => onNavigate('spotlight')}
            />
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            {
              title: "Ask Expert",
              description: "Get professional advice",
              icon: Star,
              color: "bg-gradient-to-br from-green-500 to-emerald-600",
              onClick: () => onNavigate('discover', { type: 'ask-expert' })
            },
            {
              title: "Join Community",
              description: "Connect with others",
              icon: Users,
              color: "bg-gradient-to-br from-blue-500 to-indigo-600",
              onClick: () => onNavigate('community')
            },
            {
              title: "Health Articles",
              description: "Learn something new",
              icon: BookOpen,
              color: "bg-gradient-to-br from-purple-500 to-pink-600",
              onClick: () => onNavigate('discover', { activeTab: 'articles' })
            },
            {
              title: "Shop Health",
              description: "Browse products",
              icon: Heart,
              color: "bg-gradient-to-br from-orange-500 to-red-600",
              onClick: () => onNavigate('shop')
            }
          ].map((action, index) => (
            <QuickActionCard key={action.title} {...action} index={index} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between animate-slide-up">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Community Feed</h2>
              <button 
                onClick={() => onNavigate('community')}
                className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium text-sm transition-all duration-200 hover:scale-105"
              >
                View All
              </button>
            </div>
            
            {posts.length > 0 ? (
              posts.slice(0, 3).map((post, index) => (
                <PostCard key={post.id} post={post} index={index} />
              ))
            ) : (
              // Loading state for posts
              isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <PostCardSkeleton key={index} />
                  ))}
                </div>
              ) : (
                // Empty state for posts
                <GenericEmptyState
                  icon={<MessageCircle className="text-gray-400" size={48} />}
                  title="No community posts yet"
                  description="Join the conversation by sharing your thoughts or questions"
                  actionLabel="Create Post"
                  onAction={() => onNavigate('community', { createPost: true })}
                />
              )
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Health Cosmos */}
            <div className="card-base card-padding-md animate-slide-up dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Health Cosmos</h3>
                <button 
                  onClick={() => onNavigate('discover', { activeTab: 'articles' })}
                  className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium transition-all duration-200 hover:scale-105"
                >
                  Explore
                </button>
              </div>
              <div className="space-y-3">
                {articles.length > 0 ? (
                  articles.slice(0, 2).map((article, index) => (
                    <ArticleCard key={article.id} article={article} index={index} />
                  ))
                ) : (
                  // Loading state for articles
                  isLoading ? (
                    <div className="space-y-3">
                      {[...Array(2)].map((_, index) => (
                        <ArticleCardSkeleton key={index} />
                      ))}
                    </div>
                  ) : (
                    // Empty state for articles
                    <GenericEmptyState
                      icon={<BookOpen className="text-gray-400" size={36} />}
                      title="No articles available"
                      description="Check back later for health articles"
                      actionLabel="Browse Health Knowledge"
                      onAction={() => onNavigate('discover')}
                    />
                  )
                )}
              </div>
            </div>

            {/* Upcoming Sessions */}
            <div className="card-base card-padding-md animate-slide-up dark:bg-gray-800 dark:border-gray-700" style={{ animationDelay: '100ms' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Live Sessions</h3>
                <button 
                  onClick={() => onNavigate('spotlight', { type: 'expert-sessions' })}
                  className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium transition-all duration-200 hover:scale-105"
                >
                  Join
                </button>
              </div>
              <div className="space-y-3">
                {upcomingSessions.length > 0 ? (
                  upcomingSessions.slice(1, 3).map((session, index) => (
                    <SessionCard key={session.id} session={session} index={index} />
                  ))
                ) : (
                  // Loading state for sessions
                  isLoading ? (
                    <div className="space-y-3">
                      {[...Array(2)].map((_, index) => (
                        <SessionCardSkeleton key={index} />
                      ))}
                    </div>
                  ) : (
                    // Empty state for sessions
                    <GenericEmptyState
                      icon={<Calendar className="text-gray-400" size={36} />}
                      title="No upcoming sessions"
                      description="Check back later for expert sessions"
                      actionLabel="View Expert Directory"
                      onAction={() => onNavigate('spotlight')}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hub;